import { useHydrated } from '@src/utils/useHydrated';
import { themePreviewImage } from '@src/theme/previewImages';
import { ThemeNavigationButton } from '@src/components/ThemeNavigationButton/ThemeNavigationButton';
import { useEffect, useRef, useState } from 'react';
import { loadSeason } from '@src/theme/registry';
import { preloadLandscape } from '@src/theme/preloadLandscape';
import { useTranslation } from 'react-i18next';
import { BoxTheme } from '@lub0v/santa-utils';
import { useTheme } from '@src/theme/useTheme';
import { SEASONS, type Season } from '@src/theme/season';
import { useAppSelector } from '@src/state/hooks';
import { trackHomeThemeChanged, rememberHomeTheme } from '@src/utils/themeAnalytics';
import './HomeThemeGallery.css';

export function HomeThemeGallery() {
  const { t } = useTranslation();
  const hydrated = useHydrated();
  const { season, theme, setSeason } = useTheme();
  const { status } = useAppSelector((state) => state.session);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const pending = useRef(false);
  const touch = useRef<{ x: number; y: number } | null>(null);
  useEffect(() => { rememberHomeTheme(season as BoxTheme); }, [season]);
  const index = SEASONS.indexOf(season);
  const at = (offset: number) => SEASONS[(index + offset + SEASONS.length) % SEASONS.length];
  useEffect(() => {
    if (status !== 'ready') return;
    let cancelled = false;
    // Let the selected scene paint first, then warm only its two neighbors.
    const timer = window.setTimeout(() => {
      const warmNeighbors = async () => {
        for (const offset of [-1, 1]) {
          if (cancelled) return;
          const neighbor = SEASONS[(SEASONS.indexOf(season) + offset + SEASONS.length) % SEASONS.length];
          try { const pkg = await loadSeason(neighbor); if (!cancelled) await preloadLandscape(pkg, theme); } catch { /* Selection can retry failed downloads. */ }
        }
      };
      void warmNeighbors();
    }, 500);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [season, theme, status]);
  const choose = async (next: Season) => {
    if (pending.current || status !== 'ready' || next === season) return;
    pending.current = true;
    setBusy(true);
    setError(false);
    try {
      // Temporary previews must not survive a reload or update the profile.
      await setSeason(next, { persist: false });
      trackHomeThemeChanged(next as BoxTheme);

    } catch {
      setError(true);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  };
  const disabled = !hydrated || busy || status !== 'ready';
  return (
    <section className="home-theme-gallery" aria-label={t('home.themeGallery.gallery')} data-testid="home-theme-gallery" aria-busy={busy}>
      <p className="home-theme-gallery__heading">✨ {t('home.themeGallery.heading')}</p>
      <div className="home-theme-gallery__row"
        onTouchStart={(event) => { const point = event.touches[0]; touch.current = { x: point.clientX, y: point.clientY }; }}
        onTouchEnd={(event) => {
          const start = touch.current;
          touch.current = null;
          if (!start) return;
          const point = event.changedTouches[0];
          const dx = point.clientX - start.x;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(point.clientY - start.y)) void choose(at(dx < 0 ? 1 : -1));
        }}
        onTouchCancel={() => { touch.current = null; }}>
        <ThemeNavigationButton direction="previous" className="home-theme-gallery__arrow" disabled={disabled} aria-label={t('home.themeGallery.previous')} onClick={() => void choose(at(-1))} />
        {[-1, 0, 1].map((offset) => {
          const option = at(offset);
          return <div key={option} className="home-theme-gallery__item" data-position={offset} data-selected={offset === 0}>
            <button type="button" className="home-theme-gallery__choice" aria-pressed={offset === 0} aria-label={t(`newBox.appearance.${option}`)} disabled={disabled}
              onClick={() => void choose(option)}>
              <img src={themePreviewImage(option, 'dark')} alt="" draggable={false} />
              <span className="home-theme-gallery__name">{t(`newBox.appearance.${option}`)}</span>
            </button>
          </div>;
        })}
        <ThemeNavigationButton direction="next" className="home-theme-gallery__arrow" disabled={disabled} aria-label={t('home.themeGallery.next')} onClick={() => void choose(at(1))} />
      </div>
      {error && <p className="home-theme-gallery__status" role="alert">{t('switcher.actionError')}</p>}
    </section>
  );
}
